import React, { useState, useRef, useEffect } from 'react';
import { Calendar, MapPin, Users, MoreVertical, Eye, Edit2, Trash2, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const EventList = ({ events, onEdit, onDelete, onViewOrders, loading, onCardClick, showMenu = true }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (eventId) => {
    setActiveDropdown(activeDropdown === eventId ? null : eventId);
  };

  // Helper function to get price from nested or simple structure
  const getPrice = (priceData) => {
    if (!priceData) return 0;
    
    // New format: nested object
    if (typeof priceData === 'object' && priceData.price !== undefined) {
      return priceData.price || 0;
    }
    
    // Old format: simple number
    if (typeof priceData === 'number') {
      return priceData;
    }
    
    return 0;
  };

  // Helper function to get discounted price
  const getDiscountedPrice = (priceData) => {
    if (!priceData || typeof priceData !== 'object') return null;
    
    const discounted = priceData.discountedPrice || 0;
    const regular = priceData.price || 0;
    
    // Only return discounted price if it's less than regular price and greater than 0
    if (discounted > 0 && discounted < regular) {
      return discounted;
    }
    
    return null;
  };

  // Format price display
  const getPriceDisplay = (event) => {
    const maleRegularPrice = getPrice(event.malePrice);
    const femaleRegularPrice = getPrice(event.femalePrice);
    const maleDiscountedPrice = getDiscountedPrice(event.malePrice);
    const femaleDiscountedPrice = getDiscountedPrice(event.femalePrice);
    const currency = event.currency || 'INR';

    // Use discounted prices if available, otherwise use regular prices
    const maleDisplayPrice = maleDiscountedPrice || maleRegularPrice;
    const femaleDisplayPrice = femaleDiscountedPrice || femaleRegularPrice;

    // Both free
    if (maleDisplayPrice === 0 && femaleDisplayPrice === 0) {
      return { 
        text: 'FREE', 
        isFree: true,
        hasDiscount: false,
        malePrice: { regular: 0, discounted: null },
        femalePrice: { regular: 0, discounted: null }
      };
    }

    // Check if there are active discounts
    const hasDiscount = maleDiscountedPrice || femaleDiscountedPrice;

    // Both same price
    if (maleDisplayPrice === femaleDisplayPrice) {
      return { 
        text: `${currency} ${maleDisplayPrice.toFixed(2)}`, 
        isFree: false,
        hasVariablePricing: false,
        hasDiscount,
        malePrice: { regular: maleRegularPrice, discounted: maleDiscountedPrice },
        femalePrice: { regular: femaleRegularPrice, discounted: femaleDiscountedPrice }
      };
    }

    // Different prices - show minimum
    const minPrice = Math.min(maleDisplayPrice, femaleDisplayPrice);
    return { 
      text: `${currency} ${minPrice.toFixed(2)}+`, 
      isFree: false,
      hasVariablePricing: true,
      hasDiscount,
      malePrice: { regular: maleRegularPrice, discounted: maleDiscountedPrice },
      femalePrice: { regular: femaleRegularPrice, discounted: femaleDiscountedPrice }
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="relative">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-[#F4A696] border-t-transparent"></div>
          <div className="absolute inset-0 h-16 w-16 animate-ping rounded-full border-4 border-[#F4A696] opacity-20"></div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white p-16 text-center">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC]">
          <Calendar className="h-12 w-12 text-[#F4A696]" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No events found</h3>
        <p className="text-gray-500 text-base">Create your first event to get started!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
      {events.map((event) => {
        const priceDisplay = getPriceDisplay(event);
        
        return (
          <div
            onClick={() => {
              if (onCardClick) {
                onCardClick(event);
                return;
              }
              navigate(`/admin/event-details/${event._id}`);
            }}
            key={event._id}
            className="group relative rounded-2xl bg-white overflow-hidden shadow-xl border border-[#F4A696]/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer max-w-sm"
          >
            {/* Discount Badge */}
            {priceDisplay.hasDiscount && (
              <div className="absolute top-6 right-6 z-10">
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1">
                  <span>🎉</span>
                  <span>SALE</span>
                </div>
              </div>
            )}

            {/* Padding container above image */}
            <div className="pt-3 px-3">
              {/* Clean Thumbnail - No overlays */}
              <div className="relative h-48 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden rounded-xl">
                {event.thumbnail ? (
                  <img
                    src={event.thumbnail}
                    alt={event.title}
                    className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC]">
                    <Calendar className="h-16 w-16 text-[#F4A696] opacity-30" />
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-3">
              {/* Title with Rating */}
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <h3 className="text-base font-bold text-[#1B3C5C] line-clamp-1 group-hover:text-[#F4A696] transition-colors flex-1">
                  {event.title}
                </h3>
                {/* Rating */}
                <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded-md flex-shrink-0">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span className="text-xs font-bold text-gray-900">
                    {event.overallRatings ? event.overallRatings.toFixed(1) : '0.0'}
                  </span>
                </div>
              </div>

              {/* Short Description */}
              {event.shortDescription && (
                <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-relaxed">
                  {event.shortDescription}
                </p>
              )}

              {/* Event Details Grid */}
              <div className="space-y-1.5 mb-2">
                {/* Date & Time */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-[#FFF4F0] flex-shrink-0">
                    <Calendar className="h-3 w-3 text-[#F4A696]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-900 font-semibold truncate">
                      {new Date(event.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {event.time && ` • ${event.time}`}
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-blue-50 flex-shrink-0">
                    <MapPin className="h-3 w-3 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-900 font-semibold truncate">
                      {event.city}, {event.state || event.country}
                    </p>
                  </div>
                </div>

                {/* Capacity & Registrations */}
                {event.capacity && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-purple-50 flex-shrink-0">
                      <Users className="h-3 w-3 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-900 font-semibold truncate">
                        {event.registeredCount || 0}/{event.capacity} • {event.audience || 'All'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Male/Female Count (if available) */}
                {(event.maleCount > 0 || event.femaleCount > 0) && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center w-6 h-6 rounded-lg bg-pink-50 flex-shrink-0">
                      <Users className="h-3 w-3 text-pink-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-900 font-semibold truncate">
                        M: {event.maleCount || 0} • F: {event.femaleCount || 0}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tags */}
              {event.tags && event.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {event.tags.slice(0, 2).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-md bg-[#FFF4F0] px-1.5 py-0.5 text-xs font-medium text-[#F4A696]"
                    >
                      #{tag}
                    </span>
                  ))}
                  {event.tags.length > 2 && (
                    <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                      +{event.tags.length - 2}
                    </span>
                  )}
                </div>
              )}

              {/* Bottom Section - Status, Price, Menu */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                {/* Status */}
                <span
                  className={`rounded-md px-2 py-0.5 text-xs font-bold ${
                    event.status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : event.status === 'draft'
                      ? 'bg-yellow-100 text-yellow-700'
                      : event.status === 'cancelled'
                      ? 'bg-red-100 text-red-700'
                      : event.status === 'completed'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {event.status.toUpperCase()}
                </span>

                {/* Price */}
                <div className="flex items-center gap-1.5">
                  <div className="relative group/price">
                    <p className={`text-xs font-bold ${priceDisplay.isFree ? 'text-green-600' : 'text-[#F4A696]'}`}>
                      {priceDisplay.text}
                    </p>
                    
                    {/* Price Tooltip - Shows on hover */}
                    {(priceDisplay.hasVariablePricing || priceDisplay.hasDiscount) && (
                      <div className="absolute bottom-full right-0 mb-2 hidden group-hover/price:block z-20">
                        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-xl">
                          <div className="space-y-2">
                            {/* Male Pricing */}
                            <div>
                              <div className="text-gray-400 text-[10px] mb-0.5">Male Ticket</div>
                              <div className="flex items-center gap-2">
                                {priceDisplay.malePrice.discounted ? (
                                  <>
                                    <span className="font-bold text-green-400">
                                      {event.currency} {priceDisplay.malePrice.discounted.toFixed(2)}
                                    </span>
                                    <span className="text-gray-500 line-through text-[10px]">
                                      {event.currency} {priceDisplay.malePrice.regular.toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <span className="font-bold">
                                    {event.currency} {priceDisplay.malePrice.regular.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Female Pricing */}
                            <div>
                              <div className="text-gray-400 text-[10px] mb-0.5">Female Ticket</div>
                              <div className="flex items-center gap-2">
                                {priceDisplay.femalePrice.discounted ? (
                                  <>
                                    <span className="font-bold text-green-400">
                                      {event.currency} {priceDisplay.femalePrice.discounted.toFixed(2)}
                                    </span>
                                    <span className="text-gray-500 line-through text-[10px]">
                                      {event.currency} {priceDisplay.femalePrice.regular.toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <span className="font-bold">
                                    {event.currency} {priceDisplay.femalePrice.regular.toFixed(2)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Arrow */}
                          <div className="absolute top-full right-4 -mt-1">
                            <div className="border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Three-Dot Menu */}
                  {showMenu && (
                    <div className="relative" ref={activeDropdown === event._id ? dropdownRef : null}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(event._id);
                        }}
                        className="rounded-lg p-1 hover:bg-gray-100 transition-colors"
                      >
                        <MoreVertical className="h-4 w-4 text-gray-600" />
                      </button>

                      {/* Dropdown Menu */}
                      {activeDropdown === event._id && (
                        <div className="absolute right-0 bottom-full mb-2 w-40 rounded-xl bg-white shadow-xl border border-gray-200 overflow-hidden z-10 animate-fade-in-up">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onViewOrders) {
                                onViewOrders(event);
                              } else {
                                navigate(`/admin/event/${event._id}/orders`, {
                                  state: { event }
                                });
                              }
                              setActiveDropdown(null);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View Orders
                          </button>
                          {onEdit && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(event);
                                setActiveDropdown(null);
                              }}
                              className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-[#FFF4F0] hover:text-[#F4A696] transition-colors"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              Edit Event
                            </button>
                          )}
                          {onDelete && (
                            <>
                              <div className="border-t border-gray-100"></div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(event._id);
                                  setActiveDropdown(null);
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
